import { useState, useEffect } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../firebase';

export const useJournalData = (user) => {
  const [savedJournals, setSavedJournals] = useState({});
  const [structuredEntries, setStructuredEntries] = useState({});
  const [dailyPlans, setDailyPlans] = useState({});
  const [journalMemory, setJournalMemory] = useState({});

  // Load saved journals, structured entries, daily plans, and journal memory
  useEffect(() => {
    if (!user) return;

    const journalsRef = ref(database, `journals/${user.uid}`);
    const journalsUnsubscribe = onValue(journalsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setSavedJournals(data);
    });

    const structuredRef = ref(database, `structuredEntries/${user.uid}`);
    const structuredUnsubscribe = onValue(structuredRef, (snapshot) => {
      const data = snapshot.val() || {};
      setStructuredEntries(data);
    });

    const dailyPlansRef = ref(database, `dailyPlans/${user.uid}`);
    const dailyPlansUnsubscribe = onValue(dailyPlansRef, (snapshot) => {
      const data = snapshot.val() || {};
      setDailyPlans(data);
    });

    const journalMemoryRef = ref(database, `journalMemory/${user.uid}`);
    const journalMemoryUnsubscribe = onValue(journalMemoryRef, (snapshot) => {
      const data = snapshot.val() || {};
      setJournalMemory(data);
    });

    return () => {
      journalsUnsubscribe();
      structuredUnsubscribe();
      dailyPlansUnsubscribe();
      journalMemoryUnsubscribe();
    };
  }, [user]);

  // Save structured journal entry
  const saveStructuredEntry = async (entryData) => {
    if (!user) return;

    try {
      const structuredRef = ref(database, `structuredEntries/${user.uid}/${entryData.id}`);
      await set(structuredRef, entryData);
      console.log('Structured entry saved successfully');
    } catch (error) {
      console.error('Error saving structured entry:', error);
    }
  };

  // Delete journal entry
  const deleteJournal = async (journalId) => {
    if (!user || !journalId) return;
    
    try {
      // Try to delete from both regular journals and structured entries
      const journalRef = ref(database, `journals/${user.uid}/${journalId}`);
      const structuredRef = ref(database, `structuredEntries/${user.uid}/${journalId}`);
      
      await Promise.all([
        set(journalRef, null),
        set(structuredRef, null)
      ]);
      
      return true;
    } catch (error) {
      console.error('Error deleting journal:', error);
      return false;
    }
  };

  // Get all entries for month display (both regular and structured)
  const getAllEntriesForMonth = (monthIndex, currentDate) => {
    const year = currentDate.getFullYear();
    
    const regularEntries = Object.values(savedJournals).filter(journal => {
      const journalDate = new Date(journal.createdAt || journal.date);
      return journalDate.getMonth() === monthIndex && journalDate.getFullYear() === year;
    });

    const structuredEntriesForMonth = Object.values(structuredEntries).filter(entry => {
      const entryDate = new Date(entry.createdAt || entry.date);
      return entryDate.getMonth() === monthIndex && entryDate.getFullYear() === year;
    });

    return [...regularEntries, ...structuredEntriesForMonth];
  };

  // Get structured entries for month organized by date
  const getEntriesForMonthByDate = (monthIndex, currentDate) => {
    const year = currentDate.getFullYear();
    
    const allEntries = [
      ...Object.entries(savedJournals).map(([id, journal]) => ({ 
        id, 
        ...journal, 
        type: 'freeform',
        icon: '📝'
      })),
      ...Object.entries(structuredEntries).map(([id, entry]) => ({ 
        id, 
        ...entry, 
        type: 'structured',
        icon: entry.primaryType === 'daily' ? '☀️' : entry.primaryType === 'meeting' ? '🤝' : '🚀'
      }))
    ]
    .filter(entry => {
      const entryDate = new Date(entry.createdAt || entry.date);
      return entryDate.getMonth() === monthIndex && entryDate.getFullYear() === year;
    })
    .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date));
    
    // Group by date
    const groupedByDate = allEntries.reduce((acc, entry) => {
      const date = new Date(entry.createdAt || entry.date).toLocaleDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(entry);
      return acc;
    }, {});
    
    return groupedByDate;
  };

  // Save daily plan
  const saveDailyPlan = async (date, planData) => {
    if (!user) return;

    try {
      const dateKey = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      const dailyPlanRef = ref(database, `dailyPlans/${user.uid}/${dateKey}`);
      const planEntry = {
        date: dateKey,
        processedGoal: planData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      await set(dailyPlanRef, planEntry);
      console.log('Daily plan saved successfully for', dateKey);
    } catch (error) {
      console.error('Error saving daily plan:', error);
    }
  };

  // Get daily plan for specific date
  const getDailyPlan = (date) => {
    if (!user) return null;
    
    const dateKey = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    return dailyPlans[dateKey] || null;
  };

  // Get today's daily plan
  const getTodaysDailyPlan = () => {
    const today = new Date().toISOString().split('T')[0];
    const plan = getDailyPlan(today);
    
    // Ensure the plan is from today (not cached from previous days)
    if (plan && plan.date === today) {
      console.log('Found existing plan for today:', today);
      return plan;
    }
    
    console.log('No valid plan found for today:', today);
    return null;
  };

  // Save completed daily task to journal memory
  const saveCompletedDayToMemory = async (date, completedPlan, completionData) => {
    if (!user) return;

    try {
      const dateKey = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      const memoryRef = ref(database, `journalMemory/${user.uid}/${dateKey}`);
      
      const memoryEntry = {
        date: dateKey,
        originalPlan: completedPlan,
        completionStatus: completionData.completionStatus, // 'completed', 'partial', 'not-started'
        completionPercentage: completionData.completionPercentage || 0,
        tasksCompleted: completionData.tasksCompleted || [],
        tasksNotCompleted: completionData.tasksNotCompleted || [],
        insights: completionData.insights || '',
        challenges: completionData.challenges || '',
        achievements: completionData.achievements || '',
        lessonsLearned: completionData.lessonsLearned || '',
        progressTowardsGoal: completionData.progressTowardsGoal || '',
        timeSpent: completionData.timeSpent || '',
        energyLevel: completionData.energyLevel || 'medium',
        createdAt: new Date().toISOString(),
        businessPhaseAtTime: completedPlan.businessPhaseAtTime || null
      };

      await set(memoryRef, memoryEntry);
      console.log('Completed day saved to journal memory for', dateKey);
    } catch (error) {
      console.error('Error saving completed day to memory:', error);
    }
  };

  // Get journal memory for specific date
  const getJournalMemory = (date) => {
    if (!user) return null;
    
    const dateKey = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    return journalMemory[dateKey] || null;
  };

  // Get recent journal memory (last N days)
  const getRecentJournalMemory = (days = 7) => {
    if (!user) return [];
    
    const recent = [];
    const today = new Date();
    
    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateKey = date.toISOString().split('T')[0];
      
      if (journalMemory[dateKey]) {
        recent.push(journalMemory[dateKey]);
      }
    }
    
    return recent.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Get journal memory summary for AI analysis
  const getJournalMemorySummary = (days = 30) => {
    const recentMemory = getRecentJournalMemory(days);
    
    if (recentMemory.length === 0) return null;

    const summary = {
      totalDays: recentMemory.length,
      averageCompletion: recentMemory.reduce((sum, day) => sum + (day.completionPercentage || 0), 0) / recentMemory.length,
      completedDays: recentMemory.filter(day => day.completionStatus === 'completed').length,
      partialDays: recentMemory.filter(day => day.completionStatus === 'partial').length,
      notStartedDays: recentMemory.filter(day => day.completionStatus === 'not-started').length,
      commonChallenges: recentMemory.map(day => day.challenges).filter(Boolean),
      keyAchievements: recentMemory.map(day => day.achievements).filter(Boolean),
      lessonsLearned: recentMemory.map(day => day.lessonsLearned).filter(Boolean),
      progressInsights: recentMemory.map(day => day.progressTowardsGoal).filter(Boolean),
      energyPatterns: recentMemory.map(day => ({ date: day.date, energy: day.energyLevel })),
      recentEntries: recentMemory.slice(0, 7) // Last 7 days for detailed analysis
    };

    return summary;
  };

  return {
    savedJournals,
    structuredEntries,
    dailyPlans,
    journalMemory,
    saveStructuredEntry,
    deleteJournal,
    getAllEntriesForMonth,
    getEntriesForMonthByDate,
    saveDailyPlan,
    getDailyPlan,
    getTodaysDailyPlan,
    saveCompletedDayToMemory,
    getJournalMemory,
    getRecentJournalMemory,
    getJournalMemorySummary
  };
}; 